import React, { useEffect, useRef, useState } from 'react';
import { FilterIcons } from '../../../icons/icons';
import { EMPTY_POOL_FILTERS, TalentPoolFilters } from '../../../utils/api_service/JobApplicants';

const hasAge = (f: TalentPoolFilters) => f.ageMin != null || f.ageMax != null;

export const activePoolFilterCount = (f: TalentPoolFilters) => (f.name.trim() ? 1 : 0) + (hasAge(f) ? 1 : 0);

const ageLabel = (f: TalentPoolFilters) => {
  const range = f.ageMin != null && f.ageMax != null ? `${f.ageMin}–${f.ageMax}`
    : f.ageMin != null ? `${f.ageMin}+` : `up to ${f.ageMax}`;
  return `Age ${range}${f.includeUnknownAge ? ' (incl. unknown)' : ''}`;
};

const toAge = (value: string) => (value === '' ? null : Math.max(0, Math.min(120, Number(value))));

interface PoolFiltersProps {
  value: TalentPoolFilters;
  onChange: (next: TalentPoolFilters) => void;
}

/** "Filters" button with a name + age panel. Edits are a draft until Apply. */
export const PoolFiltersButton: React.FC<PoolFiltersProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLDivElement>(null);
  const count = activePoolFilterCount(value);
  const badRange = draft.ageMin != null && draft.ageMax != null && draft.ageMin > draft.ageMax;

  useEffect(() => { if (open) setDraft(value); }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  return (
    <div className='relative' ref={ref}>
      <button
          type='button'
          onClick={() => setOpen((o) => !o)}
          className={`flex items-center gap-2 px-3.5 py-2.5 border rounded-lg text-xs font-semibold shadow-sm transition-all whitespace-nowrap ${
            count > 0
              ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
              : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
          }`}
      >
        <FilterIcons className='w-4 h-4' />
        Filters
        { count > 0 && (
          <span className='inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-600 text-white text-[10px]'>
            { count }
          </span>
        ) }
      </button>

      { open && (
        <form
            onSubmit={(e) => { e.preventDefault(); if (!badRange) apply(); }}
            className='absolute right-0 z-30 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-4 shadow-lg space-y-4'
        >
          <label className='block'>
            <span className='text-xs font-semibold text-slate-700'>Name</span>
            <input
                type='text'
                autoFocus
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder='First name, last name or email'
                className='mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500'
            />
          </label>

          <div>
            <span className='text-xs font-semibold text-slate-700'>Age</span>
            <div className='mt-1 flex items-center gap-2'>
              <input
                  type='number'
                  min={0}
                  max={120}
                  value={draft.ageMin ?? ''}
                  onChange={(e) => setDraft({ ...draft, ageMin: toAge(e.target.value) })}
                  placeholder='Min'
                  aria-label='Minimum age'
                  className='w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500'
              />
              <span className='text-slate-400 text-xs'>to</span>
              <input
                  type='number'
                  min={0}
                  max={120}
                  value={draft.ageMax ?? ''}
                  onChange={(e) => setDraft({ ...draft, ageMax: toAge(e.target.value) })}
                  placeholder='Max'
                  aria-label='Maximum age'
                  className='w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500'
              />
            </div>
            { badRange && <p className='mt-1 text-[11px] text-red-600'>Min age is above max age.</p> }
            <label className='mt-2 flex items-start gap-2 text-xs text-slate-600'>
              <input
                  type='checkbox'
                  checked={draft.includeUnknownAge}
                  disabled={!hasAge(draft)}
                  onChange={(e) => setDraft({ ...draft, includeUnknownAge: e.target.checked })}
                  className='mt-0.5'
              />
              <span>Include candidates whose CV doesn&apos;t state an age</span>
            </label>
          </div>

          <div className='flex justify-between pt-1'>
            <button
                type='button'
                onClick={() => { onChange(EMPTY_POOL_FILTERS); setOpen(false); }}
                className='text-xs font-semibold text-slate-500 hover:text-slate-700'
            >
              Clear
            </button>
            <button
                type='submit'
                disabled={badRange}
                className='rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50'
            >
              Apply
            </button>
          </div>
        </form>
      ) }
    </div>
  );
};

/** The active filters as removable chips under the search bar. */
export const PoolFilterChips: React.FC<PoolFiltersProps> = ({ value, onChange }) => {
  if (activePoolFilterCount(value) === 0) return null;
  const chip = (label: string, clear: () => void) => (
    <span className='inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100'>
      { label }
      <button type='button' onClick={clear} aria-label={`Remove ${label}`} className='text-indigo-400 hover:text-indigo-700'>
        ×
      </button>
    </span>
  );
  return (
    <div className='mt-3 flex flex-wrap items-center gap-2'>
      { value.name.trim() && chip(`Name: ${value.name.trim()}`, () => onChange({ ...value, name: '' })) }
      { hasAge(value) && chip(ageLabel(value), () => onChange({ ...value, ageMin: null, ageMax: null, includeUnknownAge: false })) }
      <button type='button' onClick={() => onChange(EMPTY_POOL_FILTERS)} className='text-xs text-slate-500 hover:text-slate-700'>
        Clear all
      </button>
    </div>
  );
};
